import { useEffect } from "react";
import {  Row } from "reactstrap";
import { DEFAULT_OPTION } from "../../../../utils/Constant";
import { useDispatch, useSelector } from "react-redux";
import { FormUtils, SelectOptionType } from "../../../../utils/FormUtils";
import {
  blocksByDistrictDataAPI,
  districtsByStateDataAPI,
  icvGetStatesDataAPICall,
  icvGetSurveyorDetailsByID,
  icvVendorNamesList,
  SurveyorCreationAPI,
  updateSurveyorAPI,
  villagesByBlocksAPI,
} from "../../../../api/icvActivityAPI";
import {  useForm } from "react-hook-form";
import { surveyorPayload } from "../../../../models/SurveyorModels";
import { LoggedUserType } from "../../../../models/GlobalModels";
import { loginSelector } from "../../../../redux/slices/loginSlice";
import { CircleSpinnerOverlay } from "react-spinner-overlay";
import CommonFields from "./CommonFields";

interface VendorCreationProps {
  setIsFormValid?: (isValid: boolean) => void;
  setSubmitFormHandler?: (handler: () => void) => void;
  setOpenModal?: (openModal: boolean) => void;
  mode?: string;
  surveyorCode?: string;
}

export const SurveyorCreation = ({
  setIsFormValid,
  setSubmitFormHandler,
  setOpenModal,
  mode,
  surveyorCode,
}: VendorCreationProps) => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors, isValid },
  } = useForm<surveyorPayload>({
    defaultValues: {
      vendorCode: "",
      surveyorType: { label: "Surveyor", value: "SURVEYOR" },
      firstName: "",
      lastName: "",
      fatherOrRelativeName: "",
      emailId: "",
      mobileNumber: "",
      address: "",
      stateId: DEFAULT_OPTION,
      districtId: DEFAULT_OPTION,
      tehsilBlockId: DEFAULT_OPTION,
      village: DEFAULT_OPTION,
      isActive: { label: "Active", value: true },
      panNumber: "",
      aadhaarNumber: "",
      bankName: "",
      branchName: "",
      bankAccountNumber: "",
      ifscCode: "",
      highestQualification: DEFAULT_OPTION,
      deviceId: "",
      panCardBase64: {
        file: null,
        preview: "",
        type: "",
      },
      aadhaarCardBase64: {
        file: null,
        preview: "",
        type: "",
      },
      bankPassbookBase64: {
        file: null,
        preview: "",
        type: "",
      },
      engagementLetterBase64: {
        file: null,
        preview: "",
        type: "",
      },
      passportPhotoBase64: {
        file: null,
        preview: "",
        type: "",
      },
      educationCertificateBase64: {
        file: null,
        preview: "",
        type: "",
      },
      allocations: [
        {
          stateId: [],
          districtId: [],
          tehsilBlockId: [],
          villageId: [],
        },
      ],
    },
    mode: "all",
  });
  const user: LoggedUserType = useSelector(loginSelector).user;
  const surveyorType = watch("surveyorType");

  const dispatch: any = useDispatch();
  const {
    vendorNamesListData,
    storeICVSurveyorDetailsById,
    isCrudLoading,
  } = useSelector((state: any) => state.icvActivity);

  const individualSurveyorData =
    storeICVSurveyorDetailsById?.data && storeICVSurveyorDetailsById?.data[0];

  const editFieldStateOptions = individualSurveyorData?.allocations.map(
    (item: any) => ({
      label: item?.stateName,
      value: item?.stateId,
    }),
  );

  const editFieldDistrictOptions = individualSurveyorData?.allocations.map(
    (item: any) => ({
      label: item.districtName,
      value: item.districtId,
      stateId: item.stateId,
      stateName: item.stateName,
    }),
  );

  const editFieldTehsilOptions = individualSurveyorData?.allocations.map(
    (item: any) => ({
      label: item.tehsilBlockName,
      value: item.tehsilBlockId,
      stateId: item.stateId,
      stateName: item.stateName,
      districtId: item.districtId,
      districtName: item.districtName,
    }),
  );

  const editFieldVillageOptions = individualSurveyorData?.allocations
    ?.filter((item: any) => item?.tehsilBlockId)
    .map((item: any) => ({
      tehsilBlockName: item.tehsilBlockName,
      tehsilBlockId: item.tehsilBlockId,
      stateId: item.stateId,
      stateName: item.stateName,
      districtId: item.districtId,
      districtName: item.districtName,
      label: item.villageName,
      value: item.villageId,
    }));

  const vendorNameOptions = FormUtils.convertVendorData(
    vendorNamesListData?.data ?? [],
  );

  // effects
  useEffect(() => {
    // Fetching the states initial data on component mount
    dispatch(icvGetStatesDataAPICall());
  }, [dispatch]);

  useEffect(() => {
    if (surveyorCode) {
      dispatch(icvGetSurveyorDetailsByID(surveyorCode));
    }
  }, []);

  const getStatusLabel = (status: boolean) => {
    if (status) {
      return { label: "Active", value: true };
    } else {
      return { label: "Inactive", value: false };
    }
  };

  // To get the districts of selected states in edit mode
  useEffect(() => {
    const stateCodes: string[] = Array.from(
      new Set(
        editFieldStateOptions?.map((item: SelectOptionType) =>
          String(item.value),
        ),
      ),
    );

    if (mode === "edit") {
      dispatch(districtsByStateDataAPI(stateCodes));
    }
  }, [individualSurveyorData]);

  // To get the blocks of selected districts in edit mode
  useEffect(() => {
    const stateCodes: string[] = Array.from(
      new Set(
        editFieldDistrictOptions?.map((item: any) => String(item.stateId)),
      ),
    );

    const districtCodes: string[] = Array.from(
      new Set(editFieldDistrictOptions?.map((item: any) => String(item.value))),
    );

    if (mode === "edit") {
      dispatch(blocksByDistrictDataAPI(stateCodes, districtCodes));
    }
  }, [individualSurveyorData]);

  // To get the villages of selected blocks in edit mode
  useEffect(() => {
    const stateCodes: string[] = Array.from(
      new Set(editFieldTehsilOptions?.map((item: any) => String(item.stateId))),
    );

    const districtCodes: string[] = Array.from(
      new Set(
        editFieldTehsilOptions?.map((item: any) => String(item.districtId)),
      ),
    );

    const blockCodes: string[] = Array.from(
      new Set(editFieldTehsilOptions?.map((item: any) => String(item.value))),
    );

    if (mode === "edit") {
      dispatch(villagesByBlocksAPI(stateCodes, districtCodes, blockCodes));
    }
  }, [individualSurveyorData]);

  useEffect(() => {
    if (mode === "edit" && individualSurveyorData) {
      const {
        vendorCode,
        surveyorType,
        firstName,
        lastName,
        fatherOrRelativeName,
        mobileNumber,
        address,
        stateId,
        stateName,
        districtId,
        districtName,
        tehsilBlockId,
        tehsilBlockName,
        village,
        isActive,
        panNumber,
        aadhaarNumber,
        bankName,
        branchName,
        bankAccountNumber,
        ifscCode,
        highestQualification,
        deviceId,
        panCardBase64,
        panCardType,
        aadhaarCardBase64,
        aadhaarCardType,
        bankPassbookBase64,
        bankPassbookType,
        engagementLetterBase64,
        engagementLetterType,
        passportPhotoBase64,
        passportPhotoType,
        educationCertificateBase64,
        educationCertificateType,
        villageId,
        emailId,
      } = individualSurveyorData;

      const matchedVendor = vendorNameOptions?.filter(
        (item: any) => item.value === vendorCode,
      );

      reset({
        vendorCode: matchedVendor,
        surveyorType:
          surveyorType === "SURVEYOR"
            ? { label: "Surveyor", value: "SURVEYOR" }
            : { label: "Vendor Individual", value: "VENDOR_INDIVIDUAL" },
        firstName: firstName,
        lastName: lastName,
        fatherOrRelativeName: fatherOrRelativeName,
        emailId: emailId,
        mobileNumber: mobileNumber,
        address: address,
        stateId: { label: stateName, value: stateId },
        districtId: { label: districtName, value: districtId },
        tehsilBlockId: { label: tehsilBlockName, value: tehsilBlockId },
        village: { label: village, value: villageId },
        isActive: getStatusLabel(isActive),
        panNumber: panNumber,
        aadhaarNumber: aadhaarNumber,
        bankName: {
          label: bankName,
          value: bankName,
        },
        branchName: branchName,
        bankAccountNumber: bankAccountNumber,
        ifscCode: ifscCode,
        highestQualification: {
          label: highestQualification,
          value: highestQualification,
        },
        deviceId: deviceId,
        panCardBase64: {
          preview: panCardBase64,
          type: panCardType,
        },
        aadhaarCardBase64: {
          preview: aadhaarCardBase64,
          type: aadhaarCardType,
        },
        bankPassbookBase64: {
          preview: bankPassbookBase64,
          type: bankPassbookType,
        },
        engagementLetterBase64: {
          preview: engagementLetterBase64,
          type: engagementLetterType,
        },
        passportPhotoBase64: {
          preview: passportPhotoBase64,
          type: passportPhotoType,
        },
        educationCertificateBase64: {
          preview: educationCertificateBase64,
          type: educationCertificateType,
        },
        allocations: [
          {
            stateId: editFieldStateOptions,
            districtId: editFieldDistrictOptions,
            tehsilBlockId: editFieldTehsilOptions,
            villageId: editFieldVillageOptions,
          },
        ],
      });
    }
  }, [individualSurveyorData]);

  useEffect(() => {
    setIsFormValid && setIsFormValid(isValid);
  }, [isValid]);

  useEffect(() => {
    setSubmitFormHandler && setSubmitFormHandler(() => handleSubmit(onSubmit));
  }, [handleSubmit]);

  useEffect(() => {
    dispatch(icvVendorNamesList());
  }, []);

  const onSubmit = (data: surveyorPayload) => {
    mode === "edit"
      ? dispatch(updateSurveyorAPI(data, user.emailId, surveyorCode))
      : dispatch(SurveyorCreationAPI(data, user.emailId));
  };

  return (
    <div className="filter-section mb-2">
      {/* Personal Information Section */}
      {isCrudLoading && (
        <CircleSpinnerOverlay
          loading={isCrudLoading}
          overlayColor="rgba(147, 166, 199, 0.1)"
          color="#5C79AA"
          zIndex={9999}
        />
      )}
      <Row>
        
        <CommonFields
          control={control}
          mode={mode}
          setValue={setValue}
          surveyorType={surveyorType}
          watch={watch}
          setOpenModal={setOpenModal}
          vendorNameOptions={vendorNameOptions}
        />
      </Row>
    </div>
  );
};
